import { useState, useEffect } from 'react';
import { clientApi } from '../services/clientApi';
import type { User } from '@ironlogic4/shared/types/users';
import { UserType } from '@ironlogic4/shared/types/users';

export interface Client {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Hook to fetch and manage clients for the current gym
 * Uses /api/gym/clients endpoint (owner and admin accessible)
 * Returns clients who can be assigned to schedules (CLIENT user type)
 *
 * @param gymId - The gym ID to fetch clients for (used for cache key, server handles scoping)
 * @returns { clients, loading, error }
 */
export function useClients(gymId: string | undefined) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gymId) {
      setClients([]);
      return;
    }

    const fetchClients = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch clients using the gym-scoped endpoint
        // Server automatically filters by user's gym
        const firstPage = await clientApi.getClients({
          limit: 100, // Server-enforced max page size
          page: 1,
        });

        const totalPages = firstPage.pagination?.totalPages ?? 1;
        const remainingPages = await Promise.all(
          Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) =>
            clientApi.getClients({ limit: 100, page: i + 2 })
          )
        );

        const allUsers: User[] = [firstPage, ...remainingPages].flatMap(
          (response) => response.data || []
        );

        // Filter for CLIENT user type and transform to Client interface.
        // Dedupe by id as a safeguard against page-boundary overlap from the
        // paginated fetch above (e.g. tied sort keys across separate page
        // requests) — a duplicate id reaching a MultiSelect crashes it.
        const clientById = new Map<string, Client>();
        for (const user of allUsers) {
          if (user.userType !== UserType.CLIENT) continue;
          clientById.set(user.id, {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
          });
        }

        setClients([...clientById.values()]);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch clients';
        console.error('Failed to fetch clients:', errorMessage);
        setError(errorMessage);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [gymId]);

  return { clients, loading, error };
}